import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { usePagination } from "@/hooks/use-pagination";
import { userConversationsQueryOptions } from "@/hooks/use-user-conversations";
import { ConversationSessionTable } from "@/components/user-conversations/conversation-session-table";
import { ConversationTranscriptDrawer } from "@/components/user-conversations/conversation-transcript-drawer";
import type { ConversationSessionItem } from "@/types/user-conversation";

function errorMessage(error: unknown): string {
  const raw = error instanceof Error ? error.message.toLowerCase() : "";
  if (raw.includes("not found")) {
    return "해당 ID의 유저를 찾을 수 없습니다.";
  }
  return "대화 이력을 불러오지 못했습니다.";
}

export default function UserConversationsPage() {
  const [userIdInput, setUserIdInput] = useState("");
  const [submittedUserId, setSubmittedUserId] = useState<string | null>(null);
  const pagination = usePagination({ initialLimit: 20 });

  const [selectedSession, setSelectedSession] =
    useState<ConversationSessionItem | null>(null);
  const drawerOpen = selectedSession !== null;

  const query = useQuery(
    useMemo(
      () =>
        userConversationsQueryOptions(submittedUserId, {
          page: pagination.page,
          limit: pagination.limit,
        }),
      [submittedUserId, pagination.page, pagination.limit]
    )
  );

  const handleSearch = useCallback(() => {
    const trimmed = userIdInput.trim();
    if (!trimmed) return;
    setSubmittedUserId(trimmed);
    pagination.setPage(1);
  }, [userIdInput, pagination]);

  const handleOpenSession = useCallback((session: ConversationSessionItem) => {
    setSelectedSession(session);
  }, []);

  const handleDrawerOpenChange = useCallback((open: boolean) => {
    if (!open) setSelectedSession(null);
  }, []);

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">AI 대화 이력</h1>
        <p className="text-muted-foreground leading-normal">
          유저 ID를 입력해 해당 유저의 모든 AI 대화 세션을 조회합니다. 세션을
          클릭하면 전체 대화를 볼 수 있습니다.
        </p>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="text-base">유저 조회</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="conversation-user-id">유저 ID</Label>
            <div className="flex gap-2">
              <Input
                id="conversation-user-id"
                value={userIdInput}
                onChange={(event) => setUserIdInput(event.target.value)}
                placeholder="users 컬렉션의 _id"
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleSearch();
                }}
              />
              <Button onClick={handleSearch} disabled={!userIdInput.trim()}>
                <Search className="mr-2 h-4 w-4" />
                조회
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {submittedUserId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">대화 세션</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 px-4 md:px-6">
            {query.isLoading && (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            )}

            {query.isError && (
              <div className="rounded border p-4 text-sm text-destructive">
                {errorMessage(query.error)}
              </div>
            )}

            {query.data && (
              <>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border bg-muted/30 p-3 text-sm">
                  <span className="font-medium">{query.data.user.email}</span>
                  {query.data.user.name && (
                    <span className="text-muted-foreground">
                      {query.data.user.name}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    세션 {query.data.meta.totalCount.toLocaleString()}개
                  </span>
                </div>

                <ConversationSessionTable
                  data={query.data.items}
                  meta={query.data.meta}
                  pagination={pagination}
                  onOpenSession={handleOpenSession}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      <ConversationTranscriptDrawer
        session={selectedSession}
        open={drawerOpen}
        onOpenChange={handleDrawerOpenChange}
      />
    </div>
  );
}
